import React, {
    useCallback, useEffect, useId, useState,
} from 'react';
import { flushSync } from 'react-dom';
import useAutocomplete from '@mui/material/useAutocomplete';
import Box from '@mui/material/Box';
import TextField from '@mui/material/TextField';
import InputAdornment from '@mui/material/InputAdornment';
import { useForkRef } from '@mui/material/utils';
import TooltipInfo from './TooltipInfo';

// Help content for the search syntax, shown via the info icon in the field.
const searchHelp = (
    <>
        <strong>Search tips</strong>
        <ul style={{ margin: '0.25rem 0 0', paddingLeft: '1.1rem' }}>
            <li>Several terms: all must match (AND)</li>
            <li>Partial text works: factor finds factorIXa</li>
            <li>Exclude with a leading minus: factor -4YZU</li>
            <li>Exclusion only matches whole name tokens, not partial</li>
        </ul>
    </>
);

// Only the first matches are rendered, growing as the user scrolls or arrows toward the end;
// rendering all ~14.5k structures is too slow (BP2-2656). 1000 holds every structure of any
// one kinase (CDK2 has the most, 917), at ~100 ms to clear the box.
const WINDOW_STEP = 1000;
// The rows MUI moves the highlight by (useAutocomplete's pageSize is 5).
const KEYBOARD_STEPS = { ArrowDown: 1, PageDown: 5 };

function SelectSearch({
    optionList, onSelection, label, hasLinks=false,
    defaultQuery='', checkForDynamicEntries=() => {},
    error, focusWhenSelected,
}) {
    const [query, setQuery] = useState('');
    const [value, setValue] = useState(null);
    const inputRef = React.useRef(null);
    const sentinelRef = React.useRef(null);
    const [windowSize, setWindowSize] = useState(WINDOW_STEP);
    const growWindow = useCallback(() => setWindowSize(windowSize + WINDOW_STEP), [windowSize]);

    useEffect(() => {
        if (defaultQuery !== '') {
            setQuery(defaultQuery);
            checkForDynamicEntries(defaultQuery);
        }
    }, []);

    // The bare `autoFocus` below only fires on mount, but this component is mounted while its
    // tab is hidden (display:none), so that initial focus is a no-op. Re-focus when the tab
    // becomes visible so the search box is ready to type into. undefined => opt out (leave the
    // one-shot autoFocus as the only behavior) for callers that don't track tab selection.
    //
    // Deferred two frames so the scroll-into-view that focusing triggers reads layout after the
    // reveal's paint, rather than forcing a second layout. One rAF still runs before that
    // frame's layout.
    useEffect(() => {
        if (!focusWhenSelected) return undefined;
        let innerFrame = 0;
        const outerFrame = requestAnimationFrame(() => {
            innerFrame = requestAnimationFrame(() => inputRef.current?.focus());
        });
        return () => {
            cancelAnimationFrame(outerFrame);
            if (innerFrame) cancelAnimationFrame(innerFrame);
        };
    }, [focusWhenSelected]);

    // ⚠️ THE SAME ID HAS TO REACH BOTH THE HOOK AND THE TextField, or the field has no
    // accessible name. `getInputProps()` returns an `id` (useAutocomplete.js: `getInputProps:
    // () => ({ id, ... })`) and it is spread onto the input below, which OVERWRITES the id
    // TextField generated for itself — and TextField's `<label htmlFor>` still points at that
    // generated one. The label then labels nothing: measured, the input presented as
    // `combobox [expanded]` with no accessible name, so `getByLabel` and any name-based query
    // could not find the structure-search box at all, and the e2e helpers had to target it
    // structurally (`input[role="combobox"]`) to tell it from the Selectivity Add type Select.
    // Passing one id to both ends puts the label back on the input.
    //
    // It is per-instance rather than the literal 'use-autocomplete' it used to be: that was a
    // single hardcoded DOM id, so two SelectSearch instances mounted at once would collide.
    const autoCompleteId = useId();
    const autoCompleteHook = useAutocomplete({
        id: autoCompleteId,
        options: optionList,
        inputValue: query,
        open: true,
        // It never closes anyway. Without this, MUI resets the highlight on the first re-render
        // after the matches change, and a window growth is one.
        disableCloseOnSelect: true,
        // multiple: true, // disabled for now to avoid "selecting" invalid entries.
        freeSolo: true,
        value,
        onChange: async (event, newValue) => {
            setQuery(query); // keep what the user entered; don't override with the new value
            await onSelection(newValue);

            // if user cancels or there are load errors autocomplete won't trigger change event
            // reset the value to null so that pressing enter or clicking protein triggers this
            // event again.
            setValue(null);
            inputRef.current.focus();
        },
        onInputChange: (event, newInputValue, reason) => {
            if ((event?.type === 'keydown' && event.key === 'Enter') || reason === 'reset') {
                // After pressing enter, the autocomplete tooling will attempt to clear the input.
                // But we want to keep the input if it's a string that doesn't match any entry.
                // So don't update the query state on "submit".
                return;
            }
            setQuery(newInputValue);
            setWindowSize(WINDOW_STEP); // every query change, a return to an earlier one included
            checkForDynamicEntries(newInputValue);
        },
        filterOptions: filterOptionsByTerms,
        getOptionLabel: (option) => option.text || '',
        groupBy: (option) => option.groupName,
    });

    // In MUI v9, getInputProps() returns a ref that must be attached via inputRef, not inputProps.
    const { ref: acInputRef, ...acInputProps } = autoCompleteHook.getInputProps();
    const mergedInputRef = useForkRef(inputRef, acInputRef);

    const { groupedOptions } = autoCompleteHook;
    const lastGroup = groupedOptions.at(-1);
    const optionCount = lastGroup ? lastGroup.index + lastGroup.options.length : 0;
    const hasMore = optionCount > windowSize;
    const renderedGroups = hasMore ? windowGroups(groupedOptions, windowSize) : groupedOptions;

    // MUI treats a highlight target missing from the DOM as disabled and skips past it, so the
    // row an arrow key moves to has to be rendered before MUI handles the key.
    function handleWindowKeys(event) {
        const jump = jumpOf(event);
        if (jump) {
            jumpTo(event, jump === 'first' ? 0 : Math.min(windowSize, optionCount) - 1);
            return;
        }
        if (!hasMore) return;
        const highlighted = highlightedIndex(inputRef.current);
        const step = KEYBOARD_STEPS[event.key];
        if (step && highlighted + step >= windowSize) {
            flushSync(growWindow);
        } else if (event.key === 'ArrowUp' && highlighted <= 0) {
            // MUI would wrap to the last match, which isn't rendered.
            event.preventDefault();
            event.defaultMuiPrevented = true;
        }
    }

    // MUI's own End would aim at the last match and walk back over every unrendered row. A
    // mouse move is the one other way MUI lets the highlight be set.
    function jumpTo(event, index) {
        event.preventDefault();
        event.defaultMuiPrevented = true;
        const row = document.getElementById(`${autoCompleteId}-option-${index}`);
        if (!row) return;
        row.dispatchEvent(new MouseEvent('mousemove', { bubbles: true }));
        if (index === 0) {
            row.closest('[role="listbox"]').scrollTop = 0; // with its group header
        } else {
            row.scrollIntoView({ block: 'nearest' });
        }
    }

    useEffect(() => {
        const sentinel = sentinelRef.current;
        if (!sentinel || typeof IntersectionObserver === 'undefined') return undefined;
        const observer = new IntersectionObserver((entries) => {
            if (entries.some((entry) => entry.isIntersecting)) growWindow();
        }, { root: sentinel.closest('[role="listbox"]'), rootMargin: '0px 0px 400px 0px' });
        observer.observe(sentinel);
        return () => observer.disconnect();
    }, [hasMore, growWindow]);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden', // Only the list items should scroll, not the autocomplete box.
            paddingTop: '1rem', // avoid the autocomplete help text from being cut off
        }}
        >
            <Box {...autoCompleteHook.getRootProps({ onKeyDown: handleWindowKeys })} sx={{ padding: '0 0 1rem 0' }}>
                <TextField
                    variant="outlined"
                    style={{ width: '100%' }}
                    // Same id the hook puts on the input (see autoCompleteId above), so
                    // InputLabel's htmlFor resolves to the element it is labelling.
                    id={autoCompleteId}
                    label={label}
                    focused
                    autoFocus
                    inputRef={mergedInputRef}
                    slotProps={{
                        htmlInput: acInputProps,
                        input: {
                            endAdornment: (
                                <InputAdornment position="end">
                                    <TooltipInfo title={searchHelp} />
                                </InputAdornment>
                            ),
                        },
                    }}
                />
            </Box>
            { !!error && error }
            {groupedOptions.length > 0 ? (
                <Box
                    sx={{
                        overflow: 'auto',
                        flex: 1,
                        minHeight: 0,
                        position: 'relative',
                        border: '2px solid #006428',
                        borderRadius: '.4rem',
                        '& li.Mui-focused': {
                            backgroundColor: '#4a8df6',
                            color: 'white',
                            cursor: 'pointer',
                        },
                    }}
                    {...autoCompleteHook.getListboxProps()}
                >
                    {renderedGroups.map(({ group, options, index }) => (
                        <React.Fragment key={group}>
                            <strong style={{ padding: '0 0 0 .2rem' }}>{group}</strong>
                            {options.map((option, ind) => {
                                const { key, ...optionProps } = autoCompleteHook.getOptionProps(
                                    { option, index: (index+ind) }
                                );
                                return (
                                    <div key={option.id} title={option.title || ''} style={{ display: 'flex' }}>
                                        <li style={{ padding: '0 1rem', flexGrow: 1, listStyle: 'none' }} {...optionProps}>
                                            {option.text}
                                        </li>
                                        {hasLinks? (
                                            <div style={{ minWidth: '6rem', display: 'flex', justifyContent: 'center' }}>
                                                {option.linkInfo? (
                                                    <a href={option.linkInfo.url} target="_blank" rel="noreferrer" style={{ color: 'inherit' }}>
                                                        {option.linkInfo.label}
                                                        <i className="fa fa-external-link" />
                                                    </a>
                                                ) : null}
                                            </div>
                                        ): null}
                                    </div>
                                );
                            })}
                        </React.Fragment>
                    ))}
                    {hasMore && <div ref={sentinelRef} aria-hidden="true" style={{ height: '1px' }} />}
                </Box>
            ) : (
                <p>No items match your search.</p>
            )}
        </Box>
    );
}

export default SelectSearch;

// The first `size` options of MUI's groups, cut from the front so each keeps its index.
function windowGroups(groups, size) {
    return groups
        .filter((group) => group.index < size)
        .map((group) => (group.index + group.options.length > size
            ? { ...group, options: group.options.slice(0, size - group.index) }
            : group));
}

// Ctrl/Cmd+Home/End, and the Mac's Cmd+Up/Down (MacBooks have no Home/End keys), jump to the
// first or last rendered row. Plain Home/End stay with the text field: MUI leaves them alone
// under freeSolo.
function jumpOf(event) {
    if ((event.ctrlKey || event.metaKey) && (event.key === 'Home' || event.key === 'End')) {
        return event.key === 'Home' ? 'first' : 'last';
    }
    if (event.metaKey && (event.key === 'ArrowUp' || event.key === 'ArrowDown')) {
        return event.key === 'ArrowUp' ? 'first' : 'last';
    }
    return null;
}

// MUI keeps the highlighted index private; the input's aria-activedescendant
// (`<id>-option-<index>`) is where it exposes it.
function highlightedIndex(input) {
    const match = /-option-(\d+)$/.exec(input?.getAttribute('aria-activedescendant') || '');
    return match ? Number(match[1]) : -1;
}

// --- Multi-term search (BP2-2592) ---------------------------------------------
// Treat the query as multiple whitespace-separated terms (AND-combined) rather
// than one literal string. A leading "-" negates a term. Per Dan's call,
// INCLUSION is broad and EXCLUSION is whole-token:
//   - POSITIVE terms match as a SUBSTRING, so incremental type-ahead keeps working
//     ("bra" -> "BRAF") and "BRAF in" keeps any BRAF row whose text contains "in".
//   - A NEGATIVE "-term" excludes a row only on a WHOLE-TOKEN match: its token(s) must
//     appear as a CONSECUTIVE run of the row's tokens. A token is a maximal run of
//     letters/digits plus the project/case-name separators "_ . + -" (ProjectNameRegex),
//     so a whole name or hyphenated label ("dfg-in", "4yzu_chains-a-b") is ONE token.
//     "/" is a DELIMITER -- it separates a "project/case" path (e.g. "F9/4YZU_chains-A-B")
//     so the case alone ("-4yzu_chains-a-b") or the project ("-f9") each match, and the
//     full path ("-f9/4yzu_chains-a-b") matches its two consecutive tokens. Matching is
//     literal apart from case/accents (separators are NOT interchangeable). A PARTIAL like
//     "-in" or "-4yzu" excludes nothing -- you type the whole label. (Like Google:
//     exclusion is word-level; we treat the hyphenated identifier as the "word".)
// This is the first increment toward the chips/faceted search described in the ticket.

// Lower-case + strip accents, matching MUI createFilterOptions' default normalization
// (ignoreCase + ignoreAccents) so existing matches behave the same. \p{Mn} removes the
// combining marks left behind by NFD decomposition (so accented letters fold to ASCII).
function normalizeForSearch(str) {
    return String(str)
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Mn}/gu, '');
}

// Token = a maximal run of identifier chars: letters, digits, and the project/case-name
// separators "_ . + -" (ProjectNameRegex). Everything else -- whitespace, "/", comma,
// colon -- is a delimiter; "/" splits a "project/case" path so each part is its own token.
// "user data f9/4yzu_chains-a-b: dfg-in" -> ["user","data","f9","4yzu_chains-a-b","dfg-in"].
function tokenize(str) {
    return str.split(/[^a-z0-9_.+-]+/).filter(Boolean);
}

// A negative "-term" excludes a row when its token(s) appear as a CONSECUTIVE run of the
// row's tokens (the term is tokenized the same way). A single identifier ("-dfg-in",
// "-4yzu_chains-a-b") is one token and must match a WHOLE token -- a PARTIAL like "-4yzu"
// or "-in" excludes nothing. A "project/case" path ("-f9/4yzu_chains-a-b") splits on "/"
// into two tokens that must match consecutively. Separators are literal (only case/accents
// are folded), so "4yzu_chains-a-b" does NOT match a "..._052525" name.
function negativeHitsTokens(term, tokens) {
    const want = tokenize(term);
    if (want.length === 0) return false;
    for (let i = 0; i + want.length <= tokens.length; i += 1) {
        if (want.every((w, j) => tokens[i + j] === w)) {
            return true;
        }
    }
    return false;
}

// Parse the query into positive (substring) terms and negative (whole-token) terms.
// "BRAF -dfg-in" -> { positive: ["braf"], negative: ["dfg-in"] }. A bare "-" is ignored.
function parseQueryTerms(inputValue) {
    const positive = [];
    const negative = [];
    normalizeForSearch(inputValue).split(/\s+/).forEach((term) => {
        if (!term || term === '-') return;
        if (term.startsWith('-')) {
            negative.push(term.slice(1));
        } else {
            positive.push(term);
        }
    });
    return { positive, negative };
}

// MUI useAutocomplete filterOptions: (options, { inputValue }) => filteredOptions.
function filterOptionsByTerms(options, { inputValue }) {
    // A non-string inputValue (e.g. undefined) means "no query" -> show everything,
    // rather than letting String(undefined) become a spurious positive term.
    if (typeof inputValue !== 'string') {
        return options;
    }
    const { positive, negative } = parseQueryTerms(inputValue);
    if (positive.length === 0 && negative.length === 0) {
        return options;
    }
    return options.filter((option) => {
        const haystack = normalizeForSearch((option.searchTerms || []).join());
        // Every positive term must appear as a substring (AND).
        if (!positive.every((term) => haystack.includes(term))) {
            return false;
        }
        // No negated term may match a consecutive run of the row's tokens.
        if (negative.length > 0) {
            const tokens = tokenize(haystack);
            if (negative.some((term) => negativeHitsTokens(term, tokens))) {
                return false;
            }
        }
        return true;
    });
}

export { filterOptionsByTerms, parseQueryTerms, WINDOW_STEP };
